import React, { useState, useEffect, useRef } from "react"
import Modal from "../index"
import ModalConfirm from "../ModalConfirm"
import Button from "../../Form/Button"
import FormGroup from "../../Form/FormGroup"
import FormGroupInline from "../../Form/FormGroupInline"
import Input from "../../Form/Input"
import TextareaAutoResize from "../../Form/TextareaAutoResize"
import Select from "../../Form/Select"
import DatePicker from "../../Form/DatePicker"
import TimePicker from "../../Form/TimePicker"
import ToggleSwitch from "../../Form/ToggleSwitch"
import BreakForm from "../../Form/BreakForm"
import api from "../../../services/api"
import { HiPlus } from "react-icons/hi"
import {
    MdOutlineAltRoute,
    MdOutlineBusiness,
    MdOutlineChatBubbleOutline,
    MdOutlineDelete,
    MdOutlineHistory,
    MdOutlineMenuBook,
    MdOutlinePsychology,
    MdOutlineSchedule,
    MdOutlineShield,
    MdOutlineSupportAgent,
    MdOutlineWavingHand,
    MdWarningAmber
} from "react-icons/md"
import { listAssignTo } from "../../../schemas/Chat"
import { useData } from "../../../contexts/dataContext"
import { toast } from "react-toastify"
import styles from "./modalAgent.module.css"
import ModalAgentVersionComparison from "./ModalAgentVersionComparison"
import { createAgentDraftFingerprint, hasAgentDraftChanges } from "./agentDraftChanges"
import { parseBooleanSelectValue } from "./booleanSelectValue"

const TABS = [
    { id: "identity", label: "Identidade", description: "Empresa e propósito", icon: <MdOutlineBusiness /> },
    { id: "start", label: "Boas-vindas", description: "Início da conversa", icon: <MdOutlineWavingHand /> },
    { id: "brain", label: "Comportamento", description: "Papel, tom e objetivos", icon: <MdOutlinePsychology /> },
    { id: "flows", label: "Intenções", description: "Detecção e coleta", icon: <MdOutlineAltRoute /> },
    { id: "responses", label: "Respostas", description: "Respostas determinísticas", icon: <MdOutlineChatBubbleOutline /> },
    { id: "knowledge", label: "Conhecimento", description: "Bases e ferramentas", icon: <MdOutlineMenuBook /> },
    { id: "safety", label: "Segurança", description: "Conteúdo e privacidade", icon: <MdOutlineShield /> },
    { id: "follow_up", label: "Retomar conversa", description: "Cliente sem resposta", icon: <MdOutlineSchedule /> },
    { id: "handoff", label: "Atendimento humano", description: "Dúvidas e transferência", icon: <MdOutlineSupportAgent /> },
    { id: "versions", label: "Versões", description: "Histórico e restauração", icon: <MdOutlineHistory /> },
]

const optionsBoolean = [
    { id: 1, value: "Sim" },
    { id: 0, value: "Não" },
]

const doubtBehaviorOptions = [
    { id: 1, value: "Passar a conversa para um atendente humano" },
    { id: 0, value: "Continuar tentando atender" },
]

const humanHelpTimingOptions = [
    { id: "autonomous", value: "Poucas vezes, tentar entender sozinho" },
    { id: "balanced", value: "Na medida certa, confirmar quando houver dúvida" },
    { id: "cautious", value: "Mais vezes, confirmar qualquer dúvida" },
]

const clarificationAttemptOptions = [
    { id: 0, value: "Não perguntar, passar direto para um atendente humano" },
    { id: 1, value: "Fazer 1 pergunta de confirmação" },
    { id: 2, value: "Fazer até 2 perguntas de confirmação" },
    { id: 3, value: "Fazer até 3 perguntas de confirmação" },
]

const knowledgeGapOptions = [
    { id: 1, value: "Informar e passar para um atendente humano" },
    { id: 0, value: "Apenas informar que não possui essa informação" },
]

const fixedResponseMatchOptions = [
    { id: "intent", value: "Quando identificar uma intenção" },
    { id: "phrase", value: "Quando encontrar uma frase" },
    { id: "intent_or_phrase", value: "Quando identificar a intenção ou encontrar a frase" },
    { id: "intent_and_phrase", value: "Somente quando a intenção e a frase coincidirem" },
]

const phraseMatchOptions = [
    { id: "word", value: "Palavras ou frases completas, recomendado" },
    { id: "exact", value: "Somente quando a mensagem for exatamente igual" },
    { id: "contains", value: "Em qualquer parte da mensagem, menos preciso" },
]

const fixedResponseDeliveryOptions = [
    { id: 0, value: "Enviar exatamente o texto informado, recomendado" },
    { id: 1, value: "Permitir que a IA adapte o texto" },
]

const weekdayOptions = [
    { id: 1, value: "Segunda-feira" },
    { id: 2, value: "Terça-feira" },
    { id: 3, value: "Quarta-feira" },
    { id: 4, value: "Quinta-feira" },
    { id: 5, value: "Sexta-feira" },
    { id: 6, value: "Sábado" },
    { id: 0, value: "Domingo" },
]

const followUpDeliveryOptions = [
    { id: "text", value: "Mensagem normal, somente com a janela aberta" },
    { id: "auto", value: "Automático, mensagem normal ou template conforme a janela" },
    { id: "whatsapp_template", value: "Template aprovado, inclusive depois de 24 horas" },
]

const followUpDelayUnits = [
    { id: "minutes", value: "Minutos" },
    { id: "hours", value: "Horas" },
    { id: "days", value: "Dias" },
]

const abandonmentDelayUnits = [
    { id: "hours", value: "Horas" },
    { id: "days", value: "Dias" },
]

const platformLabels = {
    whatsapp_api: "WhatsApp API",
    messenger: "Messenger",
    direct: "Instagram Direct",
}

const followUpStatusLabels = {
    pending: "Pendente",
    sent: "Enviada",
    responded: "Respondida",
    cancelled: "Cancelada",
    failed: "Falhou",
}

const delayUnitMinutes = { minutes: 1, hours: 60, days: 1440 }
const abandonmentUnitHours = { hours: 1, days: 24 }

const getInitialAbandonmentUnit = hours => Number(hours) >= 24 && Number(hours) % 24 === 0 ? "days" : "hours"

const confidenceByHelpTiming = {
    autonomous: 0.5,
    balanced: 0.7,
    cautious: 0.85,
}

const Textarea = props => <TextareaAutoResize {...props} minRows={6} />

const communicationProfiles = [
    { id: "amigavel", value: "Amigável e próximo", style: "casual", tone: "amigavel" },
    { id: "formal", value: "Profissional e formal", style: "profissional", tone: "formal" },
    { id: "tecnico", value: "Técnico e preciso", style: "tecnico", tone: "tecnico" },
    { id: "entusiasta", value: "Entusiasmado e vendedor", style: "entusiasta", tone: "amigavel" },
]

const ModalAgent = ({ agent = {}, fields = [], onSubmit, closeModal, onRunAutomaticEvaluation }) => {
    const [activeTab, setActiveTab] = useState("identity")
    const [tools, setTools] = useState([])
    const [knowledgeBases, setKnowledgeBases] = useState([])
    const [fixedResponses, setFixedResponses] = useState([])
    const [availableInboxes, setAvailableInboxes] = useState([])
    const [templatesByInbox, setTemplatesByInbox] = useState({})
    const [followUpMetrics, setFollowUpMetrics] = useState(null)
    const [followUpMessages, setFollowUpMessages] = useState([])
    const [agentVersions, setAgentVersions] = useState([])
    const [versionToRestore, setVersionToRestore] = useState(null)
    const [isRestoringVersion, setIsRestoringVersion] = useState(false)
    const [agentMetrics, setAgentMetrics] = useState(null)
    const [latestEvaluation, setLatestEvaluation] = useState(null)
    const [isEvaluating, setIsEvaluating] = useState(false)
    const [confirmExit, setConfirmExit] = useState(false)
    const initialFormFingerprint = useRef(null)
    const initialFixedResponsesFingerprint = useRef(agent?._id ? null : createAgentDraftFingerprint([]))
    const hasUnsavedChangesRef = useRef(false)
    const { teams, roles, users } = useData()
    const activeTabData = TABS.find(tab => tab.id === activeTab) || TABS[0]

    useEffect(() => {
        getAllTools()
        getAllKnowledgeBases()
        api.get("/chat/inboxes").then(response => setAvailableInboxes(
            (response.data?.data || []).filter(inbox => inbox?.status === "connected" && platformLabels[inbox?.platform])
        )).catch(() => setAvailableInboxes([]))
    }, [])

    useEffect(() => {
        if (!agent?._id) {
            setFixedResponses([])
            return
        }
        getAllFixedResponses()
        loadFollowUpOperations()
        loadAgentVersions()
        loadAgentMetrics()
        loadLatestEvaluation()
        const templateInboxIds = [...new Set((agent?.follow_up?.attempts || [])
            .filter(attempt => ["whatsapp_template", "auto"].includes(attempt?.delivery_type) && attempt?.inbox_id)
            .map(attempt => String(attempt.inbox_id)))]
        templateInboxIds.forEach(inboxId => {
            api.get(`/chat/inboxes/${inboxId}/templates`)
                .then(response => setTemplatesByInbox(previous => ({ ...previous, [inboxId]: response.data?.data || [] })))
                .catch(() => setTemplatesByInbox(previous => ({ ...previous, [inboxId]: [] })))
        })
    }, [agent])

    const [form, setForm] = useState({
        name: agent?.name || "",
        business_context: agent?.business_context || {
            company_name: "",
            industry: "",
            core_services: [],
            target_audience: "",
            opening_hours: [],
            addresses: [],
            payment_methods: [],
            policies: [],
            links_relevantes: []
        },

        conversation_start: agent?.conversation_start || {
            welcome_policy: "once_per_session",
            welcome_message: ""
        },

        model: agent?.model || {
            provider: "openai",
            name: "gpt-5.6-luna",
            temperature: 1
        },

        brain: {
            role: agent?.brain?.role || "",
            tone: agent?.brain?.tone || "amigavel",
            goals: agent?.brain?.goals || [],
            intents: agent?.brain?.intents || [],

            personality: agent?.brain?.personality || {
                style: "profissional",
                emoji_usage: "moderado",
                language: "pt-BR",
                first_person: true,
                formatting: "",
                closing_style: "",
            },

            constraints: agent?.brain?.constraints || []
        },

        guardrails: agent?.guardrails || {
            forbidden_topics: [],
            max_response_length: 500,
            pii_masking: true
        },

        tool_ids: agent?.tool_ids || [],
        knowledge_base_ids: agent?.knowledge_base_ids || [],

        handoff: agent?.handoff || {
            enabled: true,
            rules: []
        },

        follow_up: {
            enabled: agent?.follow_up?.enabled ?? false,
            timezone: "America/Sao_Paulo",
            allowed_weekdays: agent?.follow_up?.allowed_weekdays || [1, 2, 3, 4, 5, 6],
            start_time: agent?.follow_up?.start_time || "08:00",
            end_time: agent?.follow_up?.end_time || "20:00",
            stop_on_lead_won: agent?.follow_up?.stop_on_lead_won ?? true,
            stop_on_stage_change: agent?.follow_up?.stop_on_stage_change ?? false,
            abandonment_after_hours: agent?.follow_up?.abandonment_after_hours ?? 24,
            _abandonment_unit: getInitialAbandonmentUnit(agent?.follow_up?.abandonment_after_hours ?? 24),
            attempts: agent?.follow_up?.attempts || []
        },

        settings: {
            auto_reply: agent?.settings?.auto_reply ?? true,
            fallback_to_human: agent?.settings?.fallback_to_human ?? true,
            confidence_threshold: agent?.settings?.confidence_threshold ?? 0.7,
            clarification_attempts: agent?.settings?.clarification_attempts ?? 2,
            knowledge_gap_handoff: agent?.settings?.knowledge_gap_handoff ?? true
        },
        is_active: agent?._id ? Boolean(agent?.is_active) : false,
    })
    if (initialFormFingerprint.current === null) initialFormFingerprint.current = createAgentDraftFingerprint(form)
    const hasUnsavedChanges = hasAgentDraftChanges({
        form,
        initialForm: initialFormFingerprint.current,
        fixedResponses,
        initialFixedResponses: initialFixedResponsesFingerprint.current,
    })
    hasUnsavedChangesRef.current = hasUnsavedChanges
    const communicationProfile = communicationProfiles.find(profile => profile.style === form?.brain?.personality?.style)?.id
        || communicationProfiles.find(profile => profile.tone === form?.brain?.tone)?.id
        || "formal"
    const confidenceThreshold = Number(form?.settings?.confidence_threshold) || 0.7
    const humanHelpTiming = confidenceThreshold <= 0.55
        ? "autonomous"
        : confidenceThreshold >= 0.8 ? "cautious" : "balanced"
    const templateVariableSourceOptions = [
        { id: "fixed", value: "Valor fixo" },
        { id: "contact_name", value: "Nome do contato" },
        { id: "contact_phone", value: "Telefone do contato" },
        { id: "lead_name", value: "Nome do lead" },
        { id: "intent_name", value: "Intenção identificada" },
        ...[...new Set((form.brain?.intents || []).flatMap(intent => (
            (intent.required_fields || []).map(field => field?.name).filter(Boolean)
        )))].map(fieldName => ({ id: `collected:${fieldName}`, value: `Dado coletado, ${fieldName}` }))
    ]
    const qualityChecks = [
        { label: "Nome e empresa identificados", ok: Boolean(form.name.trim() && form.business_context?.company_name?.trim()) },
        { label: "Papel e objetivos definidos", ok: Boolean(form.brain?.role?.trim() && form.brain?.goals?.some(goal => goal.trim())) },
        { label: "Intenções com exemplos e rótulo para confirmação", ok: Boolean(form.brain?.intents?.length && form.brain.intents.every(intent => intent.name?.trim() && intent.description?.trim() && intent.clarification_label?.trim() && intent.examples?.positive?.filter(Boolean).length >= 2)) },
        { label: "Conteúdo confiável para responder", ok: Boolean(form.knowledge_base_ids?.length || form.business_context?.opening_hours?.some(Boolean) || form.business_context?.addresses?.some(Boolean) || fixedResponses.length) },
        { label: "Destino de atendimento humano configurado", ok: Boolean(form.handoff?.rules?.length) },
    ]
    const qualityScore = Math.round((qualityChecks.filter(check => check.ok).length / qualityChecks.length) * 100)
    const updateAgentActive = checked => {
        if (checked && qualityScore < 60) toast.warning("Você está ativando o agente com pontos importantes ainda pendentes na qualidade da configuração.")
        if (checked && agent?._id && latestEvaluation?.result !== "passed") {
            toast.info("Execute a verificação automática desta versão antes de ativar o agente.")
        }
        handleChange("is_active", checked)
    }

    const getAllFixedResponses = () => {
        api.get(`/ai/fixed-responses/${agent._id}`)
            .then(res => {
                const responses = res.data.data || []
                initialFixedResponsesFingerprint.current = createAgentDraftFingerprint(responses)
                setFixedResponses(responses)
            })
            .catch(() => {
                initialFixedResponsesFingerprint.current = createAgentDraftFingerprint([])
            })
    }

    const getAllTools = () => {
        api.get("/ai/tools")
            .then(res => setTools(res.data.data || []))
            .catch(err => { })
    }

    const getAllKnowledgeBases = () => {
        api.get("/ai/knowledge-bases")
            .then(res => setKnowledgeBases(res.data.data || []))
            .catch(err => { })
    }

    const loadFollowUpOperations = () => {
        if (!agent?._id) return
        Promise.all([
            api.get(`/ai/agents/${agent._id}/follow-up-metrics`),
            api.get(`/ai/agents/${agent._id}/follow-ups`),
        ]).then(([metricsResponse, messagesResponse]) => {
            setFollowUpMetrics(metricsResponse.data?.data || null)
            setFollowUpMessages(messagesResponse.data?.data || [])
        }).catch(() => {
            setFollowUpMetrics(null)
            setFollowUpMessages([])
        })
    }

    const cancelFollowUpMessage = messageId => {
        api.patch(`/ai/agents/${agent._id}/follow-ups/${messageId}/cancel`)
            .then(() => {
                toast.success("Retomada cancelada.")
                loadFollowUpOperations()
            })
            .catch(error => toast.error(error?.response?.data?.message || "Não foi possível cancelar a retomada."))
    }

    const loadAgentVersions = () => {
        if (!agent?._id) return
        api.get(`/ai/agents/${agent._id}/versions`)
            .then(response => setAgentVersions(response.data?.data || []))
            .catch(() => setAgentVersions([]))
    }

    const loadAgentMetrics = () => {
        if (!agent?._id) return
        api.get(`/ai/agents/${agent._id}/metrics`)
            .then(response => setAgentMetrics(response.data?.data || null))
            .catch(() => setAgentMetrics(null))
    }

    const loadLatestEvaluation = () => {
        if (!agent?._id) return
        api.get(`/ai/agents/${agent._id}/test-sessions`)
            .then(response => {
                const evaluation = (response.data?.data || []).find(session => (
                    session.metadata?.automated_evaluation
                    && Number(session.metadata?.agent_version) === Number(agent.version || 1)
                ))
                setLatestEvaluation(evaluation?.metadata || null)
            })
            .catch(() => setLatestEvaluation(null))
    }

    const runEvaluation = async () => {
        if (!agent?._id) return toast.info("Salve o agente antes de executar a verificação automática.")
        try {
            setIsEvaluating(true)
            const result = await onRunAutomaticEvaluation(agent._id)
            setLatestEvaluation(result)
            toast[result.result === "passed" ? "success" : "warning"](
                result.result === "passed"
                    ? `Agente aprovado em ${result.passed} de ${result.total} cenários.`
                    : result.critical_failures
                        ? `${result.critical_failures} regra obrigatória falhou. Revise o teste antes de ativar.`
                        : `O agente acertou ${result.passed} de ${result.total} cenários. Revise os itens reprovados.`
            )
        } catch (error) {
            toast.error(error.message || "Não foi possível verificar o agente.")
        } finally {
            setIsEvaluating(false)
        }
    }

    const restoreAgentVersion = async () => {
        if (!versionToRestore || isRestoringVersion) return
        try {
            setIsRestoringVersion(true)
            await api.post(`/ai/agents/${agent._id}/versions/${versionToRestore._id}/restore`)
            toast.success(`Versão ${versionToRestore.version} restaurada como uma nova versão.`)
            setVersionToRestore(null)
            closeModal?.()
        } catch (error) {
            toast.error(error?.response?.data?.message || "Não foi possível restaurar esta versão.")
        } finally {
            setIsRestoringVersion(false)
        }
    }

    const requestCloseModal = () => {
        if (hasUnsavedChangesRef.current) {
            setConfirmExit(true)
            return
        }
        closeModal?.()
    }

    const discardChangesAndClose = () => {
        setConfirmExit(false)
        closeModal?.()
    }

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }))
    }

    const updateFollowUp = data => {
        setForm(prev => ({ ...prev, follow_up: { ...prev.follow_up, ...data } }))
    }

    const updateFollowUpAttempt = (index, data) => {
        const attempts = [...(form.follow_up?.attempts || [])]
        attempts[index] = { ...attempts[index], ...data }
        updateFollowUp({ attempts })
    }

    const addFollowUpAttempt = () => {
        const attempts = form.follow_up?.attempts || []
        const platforms = [...new Set(availableInboxes.map(inbox => inbox.platform))]
        const targetPlatform = platforms.find(platform => (
            attempts.filter(attempt => !attempt.platforms?.length || attempt.platforms.includes(platform)).length < 3
        ))
        if (attempts.length >= 12 || !targetPlatform) {
            return toast.error("Cada plataforma pode ter no máximo três tentativas.")
        }
        const platformAttemptCount = attempts.filter(attempt => !attempt.platforms?.length || attempt.platforms.includes(targetPlatform)).length
        const suggestedTimes = [30, 180, 720]
        updateFollowUp({
            attempts: [...attempts, {
                after_minutes: suggestedTimes[platformAttemptCount],
                message: platformAttemptCount === 0
                    ? "Olá! Conseguiu ver minha última mensagem? Se precisar, posso continuar te ajudando por aqui."
                    : "Ainda posso ajudar você a continuar esse atendimento?",
                intent_names: [],
                platforms: [targetPlatform],
                delivery_type: "text",
                personalize_with_name: true,
                _delay_unit: platformAttemptCount === 0 ? "minutes" : "hours"
            }]
        })
    }

    const loadTemplatesByInbox = inboxId => {
        if (!inboxId || templatesByInbox[inboxId]) return
        api.get(`/chat/inboxes/${inboxId}/templates`)
            .then(response => setTemplatesByInbox(previous => ({ ...previous, [inboxId]: response.data?.data || [] })))
            .catch(() => setTemplatesByInbox(previous => ({ ...previous, [inboxId]: [] })))
    }

    const selectFollowUpTemplate = (index, templateId) => {
        const attempt = form.follow_up.attempts[index]
        const template = (templatesByInbox[attempt.inbox_id] || []).find(item => item.template_id === templateId)
        updateFollowUpAttempt(index, template ? {
            template_id: template.template_id,
            template_name: template.name,
            template_language: template.language,
            template_category: template.category,
            template_content: template.content,
        } : { template_id: "", template_name: "", template_content: null })
    }

    const getFollowUpTemplateVariables = attempt => {
        const textVariables = ["header", "body"].flatMap(component => {
        const content = attempt?.template_content?.[component]
        const matches = [...String(content?.text || "").matchAll(/{{(\w+)}}/g)]
        return [...new Set(matches.map(match => match[1]))].map(name => ({
            component,
            name,
            value: content?.variables?.find(variable => variable.name === name)?.value || "",
            source: content?.variables?.find(variable => variable.name === name)?.source || "fixed",
            fallback: content?.variables?.find(variable => variable.name === name)?.fallback || ""
        }))
        })
        const buttonVariables = (attempt?.template_content?.buttons || []).flatMap((button, index) => (
            button?.type === "COPY_CODE" || (button?.type === "URL" && /{{\w+}}/.test(button?.url || ""))
                ? [{ component: "button", name: String(index), value: button.value || "", source: button.source || "fixed", fallback: button.fallback || "" }]
                : []
        ))
        return [...textVariables, ...buttonVariables]
    }

    const updateFollowUpTemplateVariable = (index, variable, data) => {
        const attempt = form.follow_up.attempts[index]
        const templateContent = JSON.parse(JSON.stringify(attempt.template_content || {}))
        if (variable.component === "button") {
            templateContent.buttons = templateContent.buttons || []
            templateContent.buttons[Number(variable.name)] = { ...templateContent.buttons[Number(variable.name)], ...data }
            updateFollowUpAttempt(index, { template_content: templateContent })
            return
        }
        const component = templateContent[variable.component] || {}
        const variables = (component.variables || []).filter(item => item.name !== variable.name)
        variables.push({ key: `{{${variable.name}}}`, name: variable.name, value: variable.value, source: variable.source, ...data })
        templateContent[variable.component] = { ...component, variables }
        updateFollowUpAttempt(index, { template_content: templateContent })
    }

    const formatFollowUpDelay = minutes => {
        if (minutes >= 1440 && minutes % 1440 === 0) return `${minutes / 1440} dia(s)`
        if (minutes >= 60 && minutes % 60 === 0) return `${minutes / 60} hora(s)`
        return `${minutes} minuto(s)`
    }

    const getTemplatePreviewValue = variable => {
        if (!variable?.source || variable.source === "fixed") return variable?.value || `{{${variable?.name}}}`
        return variable?.fallback || {
            contact_name: "Maria",
            contact_phone: "(11) 99999-9999",
            lead_name: "Maria",
            intent_name: "interesse no produto"
        }[variable.source] || (variable.source.startsWith("collected:") ? "informação do cliente" : `{{${variable?.name}}}`)
    }

    const getFollowUpTemplatePreview = attempt => {
        const variables = getFollowUpTemplateVariables(attempt)
        const replace = (text, component) => String(text || "").replace(/{{(\w+)}}/g, (_, name) => {
            const variable = variables.find(item => item.component === component && item.name === name)
            return getTemplatePreviewValue(variable)
        })
        return [
            replace(attempt?.template_content?.header?.text, "header"),
            replace(attempt?.template_content?.body?.text, "body"),
            attempt?.template_content?.footer?.text,
            ...(attempt?.template_content?.buttons || []).map((button, index) => {
                const variable = variables.find(item => item.component === "button" && item.name === String(index))
                const detail = getTemplatePreviewValue(variable)
                return `Botão: ${button.text || button.title || button.type}${variable ? `, ${detail}` : ""}`
            })
        ].filter(Boolean).join("\n\n")
    }

    const getFollowUpTextPreview = attempt => {
        let preview = String(attempt?.message || "")
            .replace(/{{nome_contato}}|{{nome_lead}}/g, "Maria")
            .replace(/{{intencao}}/g, "seu atendimento")
            .trim()
        if (!preview || attempt?.personalize_with_name === false || /\bmaria\b/i.test(preview)) return preview
        const greeting = preview.match(/^(ol[áa]|oi|bom dia|boa tarde|boa noite)(?=[,!. ]|$)[,!. ]*/i)
        return greeting
            ? `${greeting[1]}, Maria! ${preview.slice(greeting[0].length)}`.trim()
            : `Olá, Maria! ${preview}`
    }

    const updateIntent = (index, data) => {
        const intents = [...form.brain.intents]
        intents[index] = { ...intents[index], ...data }
        setForm(prev => ({ ...prev, brain: { ...prev.brain, intents } }))
    }

    const addIntent = () => {
        setForm(prev => ({
            ...prev,
            brain: {
                ...prev.brain,
                intents: [
                    ...prev.brain.intents,
                    {
                        name: "",
                        description: "",
                        clarification_label: "",
                        knowledge_gap_behavior: "inherit",
                        examples: { positive: [], negative: [] },
                        conditions: { keywords: [], confidence_threshold: 0.7 },
                        required_fields: [],
                        actions: []
                    }
                ]
            }
        }))
    }

    const addField = (intentIndex) => {
        const intents = [...form.brain.intents]
        intents[intentIndex].required_fields.push({
            name: "",
            label: "",
            type: "text",
            required: true
        })
        setForm(prev => ({ ...prev, brain: { ...prev.brain, intents } }))
    }

    const addAction = (intentIndex) => {
        const intents = [...form.brain.intents]
        intents[intentIndex].actions.push({ type: "collect_data" })
        setForm(prev => ({ ...prev, brain: { ...prev.brain, intents } }))
    }

    const handleAddFixed = () => {
        setFixedResponses(prev => [
            ...prev,
            {
                name: "",
                triggers: [],
                intent_names: [],
                match_mode: "intent_or_phrase",
                phrase_match: "word",
                response: "",
                use_ai_formatting: false,
                priority: 0,
                is_active: true,
                valid_from: "",
                valid_until: ""
            }
        ])
    }

    const updateFixed = (index, field, value) => {
        const updated = [...fixedResponses]
        updated[index][field] = value
        setFixedResponses(updated)
    }

    const removeFixed = (index) => {
        const updated = [...fixedResponses]
        updated.splice(index, 1)
        setFixedResponses(updated)
    }

    const updateBusiness = (field, value) => {
        setForm(prev => ({
            ...prev,
            business_context: {
                ...prev.business_context,
                [field]: value
            }
        }))
    }

    const updatePersonality = (field, value) => {
        setForm(prev => ({
            ...prev,
            brain: {
                ...prev.brain,
                personality: {
                    ...prev.brain.personality,
                    [field]: value
                }
            }
        }))
    }

    const updateGuardrails = (field, value) => {
        setForm(prev => ({
            ...prev,
            guardrails: {
                ...prev.guardrails,
                [field]: value
            }
        }))
    }

    const updateCommunicationProfile = value => {
        const profile = communicationProfiles.find(item => item.id === value) || communicationProfiles[1]
        setForm(previous => ({
            ...previous,
            brain: {
                ...previous.brain,
                tone: profile.tone,
                personality: { ...previous.brain.personality, style: profile.style }
            }
        }))
    }

    const updateHandoffRule = (index, updater) => {
        const rules = [...(form.handoff?.rules || [])]
        rules[index] = typeof updater === "function"
            ? updater(rules[index])
            : { ...rules[index], ...updater }

        setForm(prev => ({ ...prev, handoff: { ...prev.handoff, rules } }))
    }

    const getFormattedHandoffRule = (rule = {}) => {
        const target = rule.target || {}
        const when = {
            intents: Array.isArray(rule.when?.intents)
                ? rule.when.intents.filter(Boolean)
                : []
        }

        if (target.assignTo === "users") {
            return {
                ...rule,
                when,
                target: {
                    assignTo: "users",
                    team_id: "",
                    role_id: "",
                    users: Array.isArray(target.users)
                        ? target.users.map(user => user?.user_id || user).filter(Boolean)
                        : []
                }
            }
        }

        if (target.assignTo === "teamAndRole") {
            return {
                ...rule,
                when,
                target: {
                    assignTo: "teamAndRole",
                    team_id: target.team_id || "",
                    role_id: target.role_id || "",
                    users: []
                }
            }
        }

        return {
            ...rule,
            when,
            target: {
                assignTo: target.assignTo || "",
                team_id: target.team_id || "",
                role_id: target.role_id || "",
                users: target.users || []
            }
        }
    }

    const prepareHandoffRulesForSubmit = () => {
        return (form.handoff?.rules || []).map(getFormattedHandoffRule)
    }

    const getDuplicatedHandoffIntent = (rules = []) => {
        const generalRulesCount = rules.filter(rule => !rule.when?.intents?.length).length
        if (generalRulesCount > 1) {
            return "qualquer intenção"
        }

        const usedIntents = new Set()

        for (const rule of rules) {
            for (const intent of rule.when?.intents || []) {
                if (usedIntents.has(intent)) {
                    return intent
                }

                usedIntents.add(intent)
            }
        }

        return ""
    }

    const handleSubmit = () => {
        if (form.follow_up?.enabled) {
            if (!(form.follow_up?.allowed_weekdays || []).length) {
                setActiveTab("follow_up")
                return toast.error("Selecione pelo menos um dia para retomar as conversas.")
            }
            if (!(form.follow_up?.attempts || []).length) {
                setActiveTab("follow_up")
                return toast.error("Adicione pelo menos uma tentativa para retomar as conversas.")
            }
            const configuredPlatforms = [...new Set(availableInboxes.map(inbox => inbox.platform))]
            const platformWithTooManyAttempts = configuredPlatforms.find(platform => (
                form.follow_up.attempts.filter(attempt => !attempt.platforms?.length || attempt.platforms.includes(platform)).length > 3
            ))
            if (platformWithTooManyAttempts) {
                setActiveTab("follow_up")
                return toast.error(`Configure no máximo três tentativas para ${platformLabels[platformWithTooManyAttempts]}.`)
            }
            const invalidAttempt = form.follow_up.attempts.find(attempt => (
                Number(attempt?.after_minutes) < 5
                || Number(attempt?.after_minutes) > 43200
                || (["text", "auto"].includes(attempt?.delivery_type || "text") && !attempt?.message?.trim())
                || (["whatsapp_template", "auto"].includes(attempt?.delivery_type) && (!attempt?.inbox_id || !attempt?.template_id || !attempt?.template_content))
                || (["whatsapp_template", "auto"].includes(attempt?.delivery_type) && getFollowUpTemplateVariables(attempt).some(variable => (
                    (!variable.source || variable.source === "fixed") && !variable.value?.trim()
                )))
            ))
            if (invalidAttempt) {
                setActiveTab("follow_up")
                return toast.error("Revise as tentativas: informe um prazo de 5 minutos a 30 dias e preencha a mensagem ou todas as variáveis do template.")
            }
        }
        const rules = prepareHandoffRulesForSubmit()
        const invalidRule = rules.find(rule => {
            if (!rule?.target?.assignTo) return true
            if (rule.target.assignTo === "teamAndRole") {
                return !rule.target.team_id && !rule.target.role_id
            }
            if (rule.target.assignTo === "users") {
                return !Array.isArray(rule.target.users) || !rule.target.users.length
            }
            return true
        })

        if (invalidRule) {
            return toast.error("Revise as regras de transferência: selecione um destino válido.")
        }

        const duplicatedIntent = getDuplicatedHandoffIntent(rules)
        if (duplicatedIntent) {
            return toast.error(`Revise as regras de transferência: a intenção "${duplicatedIntent}" não pode estar em mais de uma regra.`)
        }

        onSubmit({
            ...form,
            handoff: {
                ...form.handoff,
                rules
            },
            fixed_responses: fixedResponses
        });
    }

    return (<>
        <Modal
            icon={<span>🤖</span>}
            title="Agente IA"
            description={agent?._id ? `Editando ${agent.name || "agente"} • versão ${agent.version || 1}` : "Configure comportamento e fluxo inteligente"}
            closeModal={requestCloseModal}
            fullWidith={true}
            bodyClassName={styles.modalBody}
        >
            <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
                <div className={styles.editorLayout}>
                <aside className={styles.sidebar}>
                    <div className={styles.agentSummary}>
                        <span>{(form.name || "A").slice(0, 1).toUpperCase()}</span>
                        <div><strong>{form.name || "Novo agente"}</strong><small>{agent?._id ? `Versão ${agent.version || 1}, ${form.is_active ? "agente ativo" : "agente inativo"}` : `${form.is_active ? "Ativo" : "Inativo"}, rascunho`}</small></div>
                        <ToggleSwitch disabled={!agent?._id} checked={Boolean(form.is_active)} onChange={updateAgentActive} title={agent?._id ? (form.is_active ? "Desativar agente" : "Ativar agente") : "Salve e teste o agente antes de ativar"} aria-label={agent?._id ? (form.is_active ? "Desativar agente" : "Ativar agente") : "Salve o agente para ativar"} />
                    </div>
                    <nav className={styles.sectionNavigation} aria-label="Seções do agente">
                    {TABS.map(tab => (
                        <button type="button" key={tab.id} className={`${styles.stepButton} ${activeTab === tab.id ? styles.activeStep : ""}`} onClick={() => setActiveTab(tab.id)}>
                            <span className={styles.stepMarker}>{tab.icon}</span>
                            <span className={styles.stepText}><strong>{tab.label}</strong><small>{tab.description}</small></span>
                        </button>
                    ))}
                    </nav>
                </aside>
                <main className={styles.editorMain}>
                    <header className={styles.sectionHeader}>
                        <span className={styles.sectionIcon}>{activeTabData.icon}</span>
                        <div><small>Configuração do agente</small><h3>{activeTabData.label}</h3><p>{activeTabData.description}</p></div>
                    </header>
                    <section className={styles.stepContent}>

                {/* IDENTITY */}
                {activeTab === "identity" && (
                    <>
                        <BreakForm title="Informações básicas" showAction={false} />

                        <FormGroup labelText="Nome">
                            <Input
                                name="name"
                                placeholder="Ex: Assistente Comercial"
                                value={form.name}
                                onChange={e => handleChange("name", e.target.value)}
                            />
                        </FormGroup>

                        <BreakForm title="Contexto do Negócio" showAction={false} />

                        <FormGroup labelText="Empresa">
                            <Input
                                name="company_name"
                                placeholder="Ex: Tech Solutions Ltda"
                                value={form.business_context.company_name}
                                onChange={e => updateBusiness("company_name", e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup labelText="Segmento">
                            <Input
                                name="industry"
                                placeholder="Ex: Tecnologia / Educação / Saúde"
                                value={form.business_context.industry}
                                onChange={e => updateBusiness("industry", e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup labelText="Público-alvo">
                            <Input
                                name="target_audience"
                                placeholder="Ex: Pequenas empresas"
                                value={form.business_context.target_audience}
                                onChange={e => updateBusiness("target_audience", e.target.value)}
                            />
                        </FormGroup>

                        <section className={styles.qualityPanel}>
                            <header><span><strong>Qualidade da configuração</strong><small>Verificação automática antes da ativação</small></span><b>{qualityScore}%</b></header>
                            {qualityChecks.map(check => <p key={check.label} data-ready={check.ok}>{check.ok ? "✓" : "○"} {check.label}</p>)}
                            <div className={styles.evaluationAction}>
                                <span>
                                    <strong>{latestEvaluation?.result === "passed" ? "Versão aprovada" : latestEvaluation ? "Esta versão precisa de ajustes" : "Teste ainda não realizado"}</strong>
                                    <small>{latestEvaluation
                                        ? `${latestEvaluation.passed} de ${latestEvaluation.total} cenários corretos, ${latestEvaluation.score}%${latestEvaluation.critical_failures ? `, ${latestEvaluation.critical_failures} falha obrigatória` : ""}${latestEvaluation.usage?.calls ? `, ${latestEvaluation.usage.calls} chamadas de IA` : ""}`
                                        : "Simula conversas completas, mudanças de assunto, coleta de dados, respostas prontas e transferências."}</small>
                                    {latestEvaluation?.version_comparison?.common > 0 && <small className={styles.evaluationComparison}>
                                        Comparada à versão {latestEvaluation.version_comparison.baseline_version}: {latestEvaluation.version_comparison.improved} melhoria{latestEvaluation.version_comparison.improved === 1 ? "" : "s"}, {latestEvaluation.version_comparison.regressed} {latestEvaluation.version_comparison.regressed === 1 ? "regressão" : "regressões"}.
                                    </small>}
                                </span>
                                <Button type="button" variant="secondary" disabled={isEvaluating || !agent?._id} onClick={runEvaluation}>
                                    {isEvaluating ? "Testando..." : latestEvaluation ? "Testar novamente" : "Testar agente agora"}
                                </Button>
                            </div>
                        </section>

                        {agentMetrics && <section className={styles.agentMetrics}>
                            <header><strong>Atuação nos últimos {agentMetrics.period_days} dias</strong><small>Dados registrados pelo próprio agente</small></header>
                            <div>
                                <p><b>{agentMetrics.conversations}</b><span>Conversas</span></p>
                                <p><b>{agentMetrics.responses}</b><span>Respostas</span></p>
                                <p><b>{agentMetrics.resolved || 0}</b><span>Resolvidas</span></p>
                                <p><b>{agentMetrics.transfers}</b><span>Transferências</span></p>
                                <p><b>{agentMetrics.active_conversations || 0}</b><span>Em andamento</span></p>
                                <p><b>{agentMetrics.abandoned || 0}</b><span>Abandonadas</span></p>
                                <p><b>{agentMetrics.clarifications}</b><span>Confirmações</span></p>
                                <p><b>{agentMetrics.knowledge_gaps || 0}</b><span>Sem informação suficiente</span></p>
                                <p><b>{agentMetrics.calls}</b><span>Chamadas de IA</span></p>
                                <p><b>{Number(agentMetrics.charged_credits || 0).toLocaleString("pt-BR", { maximumFractionDigits: 4 })}</b><span>Créditos usados</span></p>
                            </div>
                            <details className={styles.metricDetails}>
                                <summary>Ver desempenho por assunto e base</summary>
                                <div className={styles.metricGroup}>
                                    <header><strong>Por assunto identificado</strong><small>Ajuda a encontrar assuntos que transferem, não encontram conteúdo ou gastam mais.</small></header>
                                    {agentMetrics.by_intent?.length ? <div className={styles.metricTable}>
                                        <div className={styles.metricTableHeader}><span>Assunto</span><span>Respostas</span><span>Transferências</span><span>Sem informação</span><span>Tokens</span><span>Custo</span></div>
                                        {agentMetrics.by_intent.map(intent => <div key={intent.name}>
                                            <strong>{intent.name}</strong>
                                            <span>{intent.responses}</span>
                                            <span>{intent.transfers}</span>
                                            <span>{intent.knowledge_gaps}</span>
                                            <span>{Number(intent.total_tokens || 0).toLocaleString("pt-BR")}</span>
                                            <span>US$ {Number(intent.provider_cost_usd || 0).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 6 })}</span>
                                        </div>)}
                                    </div> : <p className={styles.metricEmpty}>As próximas respostas do agente começarão a preencher este relatório.</p>}
                                </div>
                                <div className={styles.metricGroup}>
                                    <header><strong>Por base de conhecimento</strong><small>O custo é dividido proporcionalmente quando uma resposta consulta mais de uma base.</small></header>
                                    {agentMetrics.by_knowledge_base?.length ? <div className={styles.metricTable}>
                                        <div className={styles.metricTableHeader}><span>Base</span><span>Usos</span><span>Sem informação</span><span>Busca direta</span><span>Força relativa</span><span>Custo estimado</span></div>
                                        {agentMetrics.by_knowledge_base.map(base => <div key={base.knowledge_base_id}>
                                            <strong>{base.name}</strong>
                                            <span>{base.responses}</span>
                                            <span>{base.knowledge_gaps}</span>
                                            <span>{base.direct_searches}</span>
                                            <span>{base.average_relevance}%</span>
                                            <span>US$ {Number(base.estimated_provider_cost_usd || 0).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 6 })}</span>
                                        </div>)}
                                    </div> : <p className={styles.metricEmpty}>Ainda não existem respostas rastreadas por base. O histórico anterior continuará nos totais gerais.</p>}
                                </div>
                            </details>
                        </section>}

                        <BreakForm title="Informações usadas nas respostas" showAction={false} />
                        <FormGroupInline>
                            <FormGroup labelText="Horários de atendimento">
                                <Textarea value={(form.business_context.opening_hours || []).join("\n")} placeholder={'Ex:\nSegunda a sexta, das 8h às 18h\nSábado, das 8h às 12h'} onChange={event => updateBusiness("opening_hours", event.target.value.split("\n"))} />
                                <small className={styles.fieldExplanation}>Uma informação por linha.</small>
                            </FormGroup>
                            <FormGroup labelText="Endereços">
                                <Textarea value={(form.business_context.addresses || []).join("\n")} placeholder={'Ex:\nUnidade Centro, Rua Exemplo, 100'} onChange={event => updateBusiness("addresses", event.target.value.split("\n"))} />
                                <small className={styles.fieldExplanation}>Informe o nome da unidade e o endereço completo.</small>
                            </FormGroup>
                        </FormGroupInline>

                        <FormGroupInline>
                            <FormGroup labelText="Formas de pagamento">
                                <Textarea value={(form.business_context.payment_methods || []).join("\n")} placeholder={'Ex:\nPix\nCartão em até 6 vezes'} onChange={event => updateBusiness("payment_methods", event.target.value.split("\n"))} />
                            </FormGroup>
                            <FormGroup labelText="Políticas e condições importantes">
                                <Textarea value={(form.business_context.policies || []).join("\n")} placeholder={'Ex:\nCancelamentos devem ser solicitados com 24 horas de antecedência'} onChange={event => updateBusiness("policies", event.target.value.split("\n"))} />
                            </FormGroup>
                        </FormGroupInline>

                    </>
                )}

                {/* CONVERSATION START */}
                {activeTab === "start" && (
                    <>
                        <BreakForm title="Boas-vindas" showAction={false} />

                        <FormGroup labelText="Quando enviar">
                            <Select
                                name="welcome_policy"
                                selected={form.conversation_start.welcome_policy}
                                options={[
                                    { id: "once_per_session", value: "Uma vez por sessão" },
                                    { id: "once_per_conversation", value: "Uma vez por conversa" },
                                    { id: "disabled", value: "Não enviar automaticamente" }
                                ]}
                                onChange={value => handleChange("conversation_start", {
                                    ...form.conversation_start,
                                    welcome_policy: value
                                })}
                            />
                            <small style={{ color: "var(--text-secondary)" }}>
                                A mensagem é combinada com a primeira resposta útil, mesmo quando o lead não começa com “oi”.
                            </small>
                        </FormGroup>

                        {form.conversation_start.welcome_policy !== "disabled" && (
                            <FormGroup labelText="Mensagem de boas-vindas">
                                <Textarea
                                    name="welcome_message"
                                    rows={4}
                                    maxLength={500}
                                    placeholder="Ex: Olá! Sou a assistente virtual da empresa e vou te ajudar por aqui."
                                    value={form.conversation_start.welcome_message}
                                    onChange={event => handleChange("conversation_start", {
                                        ...form.conversation_start,
                                        welcome_message: event.target.value
                                    })}
                                />
                                <small style={{ color: "var(--text-secondary)" }}>
                                    Se ficar vazia, será usada uma apresentação padrão com o nome da empresa.
                                </small>
                            </FormGroup>
                        )}
                    </>
                )}

                {/* BRAIN */}
                {activeTab === "brain" && (
                    <>
                        <BreakForm title="Comportamento do agente" showAction={false} />

                        <FormGroup labelText="Papel e responsabilidade do agente">
                            <Textarea
                                name="role"
                                placeholder="Ex: Você é um consultor comercial responsável por entender a necessidade do lead, esclarecer dúvidas com base nas informações disponíveis e indicar o próximo passo correto."
                                rows={5}
                                value={form.brain.role}
                                onChange={e =>
                                    setForm(prev => ({
                                        ...prev,
                                        brain: { ...prev.brain, role: e.target.value }
                                    }))
                                }
                            />
                            <small style={{ color: "var(--text-secondary)" }}>Esta instrução faz parte do contexto usado para gerar todas as respostas.</small>
                        </FormGroup>

                        <FormGroup labelText="Estilo de comunicação">
                            <Select
                                name="communication_profile"
                                placeholder="Selecione como o agente deve conversar"
                                selected={communicationProfile}
                                options={communicationProfiles.map(({ id, value }) => ({ id, value }))}
                                onChange={updateCommunicationProfile}
                            />
                            <small style={{ color: "var(--text-secondary)" }}>Define em conjunto o tom e a personalidade do texto, evitando configurações contraditórias.</small>
                        </FormGroup>

                        <FormGroup labelText="Objetivos">
                            <Textarea
                                name="goals"
                                placeholder="Ex: Capturar leads, Responder dúvidas, Agendar reuniões"
                                rows={5}
                                value={form.brain.goals.join("\n")}
                                onChange={e =>
                                    setForm(prev => ({
                                        ...prev,
                                        brain: {
                                            ...prev.brain,
                                            goals: e.target.value.split("\n")
                                        }
                                    }))
                                }
                            />
                        </FormGroup>

                        <BreakForm title="Detalhes da comunicação" showAction={false} />

                        <FormGroup labelText="Uso de emojis">
                                <Select
                                    name="emoji_usage"
                                    placeholder="Selecione o uso de emojis"
                                    selected={form.brain.personality.emoji_usage}
                                    options={[
                                        { id: "nenhum", value: "Nenhum" },
                                        { id: "moderado", value: "Moderado" },
                                        { id: "frequente", value: "Frequente" }
                                    ]}
                                    onChange={val => updatePersonality("emoji_usage", val)}
                                />
                        </FormGroup>

                        <FormGroup labelText="Regras de Formatação">
                            <Textarea
                                name="formatting"
                                placeholder="Ex: Use parágrafos curtos"
                                rows={5}
                                value={form.brain.personality.formatting}
                                onChange={e => updatePersonality("formatting", e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup labelText="Estilo de Encerramento">
                            <Textarea
                                name="closing_style"
                                placeholder="Ex: Nunca termine suas respostas com frases genéricas como 'Estou aqui para ajudar'. Sempre encerre com uma pergunta proativa ou um próximo passo útil"
                                rows={5}
                                value={form.brain.personality.closing_style}
                                onChange={e => updatePersonality("closing_style", e.target.value)}
                            />
                        </FormGroup>
                    </>
                )}

                {/* AI FLOW (INTENTS) */}
                {activeTab === "flows" && (
                    <>
                        <BreakForm
                            title="Intenções e Fluxos Inteligentes"
                            iconAction={<><HiPlus /> Adicionar intenção</>}
                            actionVariant="secondary"
                            handleAction={addIntent}
                            compactAction={false}
                            showAction
                        />

                        <p style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginBottom: 20 }}>
                            Defina o que o bot deve identificar na fala do usuário e quais dados ele deve coletar.
                        </p>
                        <div className={styles.guidance}>
                            <strong>Não é necessário criar uma intenção de boas-vindas.</strong>
                            <span>A abertura configurada na seção “Boas-vindas” já é aplicada automaticamente à primeira resposta útil, mesmo quando o lead começa direto com uma pergunta.</span>
                        </div>

                        {form.brain.intents.map((intent, index) => (
                            <div key={index} className={styles.configurationCard}>
                                {/* HEADER DA INTENÇÃO */}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 15 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <span style={{ background: "var(--brand-primary)", color: "var(--text-on-accent)", padding: "2px 8px", borderRadius: 6, fontSize: "0.75rem", fontWeight: "bold" }}>
                                            #{index + 1}
                                        </span>
                                        <h4 className="purple" style={{ margin: 0, fontSize: "1rem" }}>Configuração da Intenção</h4>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="neutral"
                                        size="sm"
                                        fullWidth={false}
                                        onClick={() => {
                                            const newIntents = form.brain.intents.filter((_, i) => i !== index);
                                            setForm(prev => ({ ...prev, brain: { ...prev.brain, intents: newIntents } }));
                                        }}
                                    >
                                        <MdOutlineDelete size={18} /> Remover
                                    </Button>
                                </div>

                                <FormGroupInline className={styles.intentFieldRow}>
                                    <FormGroup labelText="Nome da Intenção (ID)">
                                        <Input
                                            placeholder="Ex: pedido_suporte"
                                            value={intent.name}
                                            onChange={e => updateIntent(index, { name: e.target.value })}
                                        />
                                    </FormGroup>
                                </FormGroupInline>

                                <FormGroup className={styles.intentStandaloneField} labelText="Descrição da intenção">
                                    <Textarea
                                        placeholder="Ex: O lead quer cancelar ou interromper a matrícula atual."
                                        rows={3}
                                        value={intent.description || ""}
                                        onChange={event => updateIntent(index, { description: event.target.value })}
                                    />
                                </FormGroup>

                                <FormGroupInline className={styles.intentFieldRow}>
                                    <FormGroup labelText="Como chamar este assunto para o cliente?">
                                        <Input
                                            placeholder="Ex: cancelamento da matrícula"
                                            value={intent.clarification_label || ""}
                                            onChange={event => updateIntent(index, { clarification_label: event.target.value })}
                                        />
                                        <small className={styles.fieldExplanation}>Usado em perguntas como: “Você quer falar sobre cancelamento da matrícula, certo?”.</small>
                                    </FormGroup>
                                    <FormGroup labelText="Se faltar informação para responder">
                                        <Select
                                            selected={intent.knowledge_gap_behavior || "inherit"}
                                            options={[
                                                { id: "inherit", value: "Usar a regra geral do agente" },
                                                { id: "inform", value: "Avisar que não encontrou a informação" },
                                                { id: "transfer", value: "Avisar e transferir para um atendente humano" }
                                            ]}
                                            onChange={value => updateIntent(index, { knowledge_gap_behavior: value })}
                                        />
                                    </FormGroup>
                                </FormGroupInline>

                                <FormGroupInline className={styles.intentFieldRow}>
                                    <FormGroup labelText="Exemplos que pertencem a esta intenção">
                                        <Textarea
                                            placeholder={`Ex:\nQuero cancelar minha matrícula\nNão vou continuar o curso\nComo faço para interromper?`}
                                            rows={5}
                                            value={(intent.examples?.positive || []).join("\n")}
                                            onChange={event => updateIntent(index, {
                                                examples: {
                                                    ...(intent.examples || {}),
                                                    positive: event.target.value.split("\n")
                                                }
                                            })}
                                        />
                                        <small style={{ color: "var(--text-secondary)" }}>Um exemplo por linha.</small>
                                    </FormGroup>

                                    <FormGroup labelText="Exemplos que não pertencem">
                                        <Textarea
                                            placeholder={`Ex:\nQuero trocar de curso\nQuando termina minha matrícula?\nPreciso da segunda via`}
                                            rows={5}
                                            value={(intent.examples?.negative || []).join("\n")}
                                            onChange={event => updateIntent(index, {
                                                examples: {
                                                    ...(intent.examples || {}),
                                                    negative: event.target.value.split("\n")
                                                }
                                            })}
                                        />
                                        <small style={{ color: "var(--text-secondary)" }}>Use frases parecidas que costumam confundir a IA.</small>
                                    </FormGroup>
                                </FormGroupInline>

                                <FormGroup labelText="Instruções específicas para esta intenção">
                                    <Textarea
                                        placeholder="O que a IA deve fazer quando identificar isso?"
                                        value={intent.instructions}
                                        onChange={e => updateIntent(index, { instructions: e.target.value })}
                                    />
                                </FormGroup>

                                {/* SUB-SEÇÃO: CAMPOS */}
                                <div style={{ background: "var(--surface-muted)", borderRadius: 8, padding: 12, marginTop: 15, border: "var(--border-subtle)" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                        <h5 style={{ margin: 0, fontSize: "0.85rem", color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                            📋 Dados para Coletar
                                        </h5>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            size="sm"
                                            fullWidth={false}
                                            onClick={() => addField(index)}
                                        >
                                            <HiPlus /> Adicionar campo
                                        </Button>
                                    </div>

                                    {intent.required_fields?.map((field, fIdx) => (
                                        <div key={fIdx} className={styles.fieldRow}>
                                            <FormGroup labelText="Identificador" style={{ marginBottom: 0 }}>
                                                <Input
                                                    placeholder="Ex: email"
                                                    value={field.name}
                                                    onChange={e => {
                                                        const fields = [...intent.required_fields];
                                                        fields[fIdx].name = e.target.value;
                                                        updateIntent(index, { required_fields: fields });
                                                    }}
                                                />
                                            </FormGroup>
                                            <FormGroup labelText="Tipo" style={{ marginBottom: 0 }}>
                                                <Select
                                                    selected={field.type}
                                                    options={[
                                                        { id: "text", value: "Texto" },
                                                        { id: "email", value: "E-mail" },
                                                        { id: "phone", value: "Telefone" },
                                                        { id: "number", value: "Número" },
                                                        { id: "date", value: "Data" }
                                                    ]}
                                                    onChange={val => {
                                                        const fields = [...intent.required_fields];
                                                        fields[fIdx].type = val;
                                                        updateIntent(index, { required_fields: fields });
                                                    }}
                                                />
                                            </FormGroup>
                                            <FormGroup labelText="Nome exibido ao cliente" style={{ marginBottom: 0 }}>
                                                <Input
                                                    placeholder="Ex: seu melhor e-mail"
                                                    value={field.label || ""}
                                                    onChange={event => {
                                                        const fields = [...intent.required_fields]
                                                        fields[fIdx].label = event.target.value
                                                        updateIntent(index, { required_fields: fields })
                                                    }}
                                                />
                                            </FormGroup>
                                            <FormGroup labelText="Precisa ser informado?" style={{ marginBottom: 0 }}>
                                                <Select
                                                    options={optionsBoolean.map(option => ({ ...option, value: option.id ? "Sim, é obrigatório" : "Não, é opcional" }))}
                                                    selected={Number(field.required !== false)}
                                                    onChange={value => {
                                                        const fields = [...intent.required_fields]
                                                        fields[fIdx].required = parseBooleanSelectValue(value)
                                                        updateIntent(index, { required_fields: fields })
                                                    }}
                                                    allowDeselectAll={false}
                                                />
                                                <small className={styles.fieldExplanation}>Campos opcionais são aproveitados quando o cliente informar, mas não impedem o encaminhamento.</small>
                                            </FormGroup>
                                            <FormGroup labelText="Validação">
                                                <Select
                                                    selected={field.validation || "default"}
                                                    options={[
                                                        { id: "default", value: "Validar conforme o tipo do dado" },
                                                        { id: "cpf", value: "CPF, com verificação dos dígitos" },
                                                        { id: "cnpj", value: "CNPJ, com verificação dos dígitos" },
                                                        { id: "cep", value: "CEP, com 8 dígitos" }
                                                    ]}
                                                    onChange={value => {
                                                        const fields = [...intent.required_fields]
                                                        fields[fIdx].validation = value === "default" ? "" : value
                                                        updateIntent(index, { required_fields: fields })
                                                    }}
                                                />
                                            </FormGroup>
                                            <Button
                                                type="button"
                                                variant="neutral"
                                                size="sm"
                                                fullWidth={false}
                                                aria-label={`Remover campo ${fIdx + 1}`}
                                                onClick={() => {
                                                    const fields = intent.required_fields.filter((_, i) => i !== fIdx);
                                                    updateIntent(index, { required_fields: fields });
                                                }}
                                            >
                                                <MdOutlineDelete size={18} />
                                            </Button>
                                        </div>
                                    ))}
                                </div>

                                {/* SUB-SEÇÃO: AÇÕES */}
                                <div style={{ background: "var(--brand-secondary-alpha)", borderRadius: 8, padding: 12, marginTop: 10, border: "1px solid var(--brand-secondary-alpha)" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                        <h5 style={{ margin: 0, fontSize: "0.85rem", color: "var(--brand-secondary)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                            ⚡ Ações após coleta
                                        </h5>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            size="sm"
                                            fullWidth={false}
                                            onClick={() => addAction(index)}
                                        >
                                            <HiPlus /> Adicionar ação
                                        </Button>
                                    </div>

                                    {intent.actions?.map((action, aIdx) => (
                                        <div key={aIdx} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
                                            <div style={{ flex: 1 }}>
                                                <Select
                                                    selected={action.type}
                                                    options={[
                                                        { id: "collect_data", value: "Apenas coletar" },
                                                        // { id: "call_tool", value: "Chamar Ferramenta" },
                                                        { id: "transfer", value: "Transferir Humano" }
                                                    ]}
                                                    onChange={val => {
                                                        const actions = [...intent.actions];
                                                        actions[aIdx].type = val;
                                                        updateIntent(index, { actions });
                                                    }}
                                                />
                                            </div>

                                            {action.type === "call_tool" && (
                                                <div style={{ flex: 1 }}>
                                                    <Select
                                                        selected={action.tool_id}
                                                        options={tools.map(t => ({ id: t._id, value: t.name }))}
                                                        onChange={val => {
                                                            const actions = [...intent.actions];
                                                            actions[aIdx].tool_id = val;
                                                            updateIntent(index, { actions });
                                                        }}
                                                    />
                                                </div>
                                            )}

                                            <Button
                                                type="button"
                                                variant="neutral"
                                                size="sm"
                                                fullWidth={false}
                                                aria-label={`Remover ação ${aIdx + 1}`}
                                                onClick={() => {
                                                    const actions = intent.actions.filter((_, i) => i !== aIdx);
                                                    updateIntent(index, { actions });
                                                }}
                                            >
                                                <MdOutlineDelete size={18} />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </>
                )}

                {/* RESPONSES */}
                {activeTab === "responses" && (
                    <>
                        <BreakForm
                            title="Respostas prontas"
                            iconAction={<><HiPlus /> Adicionar resposta</>}
                            actionVariant="secondary"
                            handleAction={handleAddFixed}
                            compactAction={false}
                        />

                        {fixedResponses.length === 0 && (
                            <div className={styles.fixedResponseEmptyState}>
                                <span><MdOutlineChatBubbleOutline /></span>
                                <div>
                                    <strong>Nenhuma resposta pronta cadastrada</strong>
                                    <p>Use respostas prontas para informações que precisam ser enviadas de forma previsível, como horários, endereços, links e procedimentos.</p>
                                </div>
                                <Button variant="secondary" fullWidth={false} onClick={handleAddFixed}>
                                    <HiPlus /> Criar primeira resposta
                                </Button>
                            </div>
                        )}

                        {fixedResponses.map((item, index) => (
                            <div
                                key={item._id || index}
                                className={styles.configurationCard}
                            >
                                <header className={styles.cardHeader}>
                                    <h4>Resposta pronta #{index + 1}</h4>
                                    <Button type="button" variant="neutral" size="sm" fullWidth={false} onClick={() => removeFixed(index)}><MdOutlineDelete /> Remover</Button>
                                </header>
                                <FormGroup>
                                    <FormGroup labelText="Nome interno">
                                        <Input
                                            placeholder="Ex: Horário de atendimento"
                                            value={item.name || ""}
                                            onChange={(e) =>
                                                updateFixed(index, "name", e.target.value)
                                            }
                                        />
                                    </FormGroup>

                                    <FormGroupInline className={styles.fixedResponseMetaRow}>
                                        <FormGroup labelText="Situação">
                                            <Select
                                                options={optionsBoolean.map(option => ({ ...option, value: option.id ? "Ativa" : "Inativa" }))}
                                                selected={Number(item.is_active ?? true)}
                                                onChange={(value) => updateFixed(index, "is_active", value)}
                                            />
                                        </FormGroup>
                                        <FormGroup labelText="Prioridade">
                                            <Input
                                                type="number"
                                                min={-100}
                                                max={100}
                                                value={Number(item.priority || 0)}
                                                onChange={(event) => updateFixed(index, "priority", Number(event.target.value))}
                                            />
                                            <small className={styles.fieldExplanation}>Se mais de uma resposta combinar, a maior prioridade será usada.</small>
                                        </FormGroup>
                                    </FormGroupInline>

                                    <FormGroup labelText="Quando usar esta resposta?">
                                        <Select
                                            options={fixedResponseMatchOptions}
                                            selected={item.match_mode || "intent_or_phrase"}
                                            onChange={(value) => updateFixed(index, "match_mode", value)}
                                        />
                                    </FormGroup>

                                    {["intent", "intent_or_phrase", "intent_and_phrase"].includes(item.match_mode || "intent_or_phrase") && (
                                        <FormGroup labelText="Intenções relacionadas">
                                            <Select
                                                name="Intenções relacionadas"
                                                placeholder="Selecione uma ou mais intenções"
                                                options={(form.brain?.intents || []).filter(intent => intent?.name).map(intent => ({
                                                    id: intent.name,
                                                    value: intent.description ? `${intent.name}, ${intent.description}` : intent.name
                                                }))}
                                                selected={item.intent_names || []}
                                                multiple
                                                showOptionAll={false}
                                                onChange={(value) => updateFixed(index, "intent_names", value)}
                                            />
                                        </FormGroup>
                                    )}

                                    {["phrase", "intent_or_phrase", "intent_and_phrase"].includes(item.match_mode || "intent_or_phrase") && (
                                        <>
                                            <FormGroup labelText="Frases que podem ativar a resposta">
                                                <Textarea
                                                    placeholder={`Ex:\nQual é o horário de atendimento?\nQue horas vocês abrem?`}
                                                    value={(item.triggers || []).join("\n")}
                                                    onChange={(event) => updateFixed(index, "triggers", event.target.value.split("\n"))}
                                                />
                                                <small className={styles.fieldExplanation}>Informe uma frase por linha. Evite palavras muito curtas ou genéricas.</small>
                                            </FormGroup>
                                            <FormGroup labelText="Como comparar as frases?">
                                                <Select
                                                    options={phraseMatchOptions}
                                                    selected={item.phrase_match || "word"}
                                                    onChange={(value) => updateFixed(index, "phrase_match", value)}
                                                />
                                            </FormGroup>
                                        </>
                                    )}

                                    <FormGroup labelText="Texto da resposta">
                                        <Textarea
                                            placeholder="Resposta que será enviada ao cliente"
                                            value={item.response || ""}
                                            onChange={(e) =>
                                                updateFixed(index, "response", e.target.value)
                                            }
                                        />
                                    </FormGroup>

                                    <FormGroup labelText="Como enviar?">
                                        <Select
                                            options={fixedResponseDeliveryOptions}
                                            selected={Number(item.use_ai_formatting ?? false)}
                                            onChange={(value) =>
                                                updateFixed(index, "use_ai_formatting", value)
                                            }
                                        />
                                        <small className={styles.fieldExplanation}>A adaptação usa IA, gera custo e pode alterar a redação, mas nunca deve mudar as informações.</small>
                                    </FormGroup>

                                    <FormGroup labelText="Período de validade, opcional">
                                        <DatePicker
                                            name="Sempre disponível"
                                            dateStart={String(item.valid_from || "").slice(0, 10)}
                                            dateEnd={String(item.valid_until || "").slice(0, 10)}
                                            multiple
                                            onChange={(dateStart, dateEnd) => {
                                                const updated = [...fixedResponses]
                                                updated[index] = {
                                                    ...updated[index],
                                                    valid_from: dateStart || "",
                                                    valid_until: dateEnd || ""
                                                }
                                                setFixedResponses(updated)
                                            }}
                                        />
                                        <small className={styles.fieldExplanation}>Deixe vazio para manter a resposta disponível sem prazo.</small>
                                    </FormGroup>
                                </FormGroup>
                            </div>
                        ))}
                    </>
                )}

                {/* KNOWLEDGE */}
                {activeTab === "knowledge" && (
                    <>
                        <BreakForm title="Base de conhecimento" showAction={false} />
                        <FormGroup labelText="Selecionar bases">
                            <Select
                                name="Base de conhecimento"
                                placeholder="Pesquise uma opção"
                                options={knowledgeBases.map(kb => ({
                                    id: kb._id,
                                    value: kb.name
                                }))}
                                selected={form.knowledge_base_ids}
                                onChange={(value) =>
                                    handleChange("knowledge_base_ids", value)
                                }
                                multiple
                            />
                        </FormGroup>
                        <section className={styles.knowledgeStatus}>
                            {!form.knowledge_base_ids?.length && <p>Nenhuma base selecionada. O agente usará apenas as informações estruturadas e respostas prontas.</p>}
                            {knowledgeBases.filter(kb => form.knowledge_base_ids?.includes(kb._id)).map(kb => <div key={kb._id}>
                                <span><strong>{kb.name}</strong><small>Atualizada em {new Date(kb.updated_at || kb.created_at).toLocaleDateString("pt-BR")}</small></span>
                                <b data-status={kb.status}>{kb.status === "ready" ? "Pronta" : kb.status === "error" ? "Com erro" : "Processando"}</b>
                            </div>)}
                        </section>
                    </>
                )}

                {/* SETTINGS & HANDOFF */}
                {activeTab === "safety" && (
                    <>
                        <BreakForm title="Segurança" showAction={false} />

                        <FormGroup labelText="Tópicos proibidos">
                            <Textarea
                                value={form.guardrails.forbidden_topics.join("\n")}
                                placeholder={`Ex:
política
religião
conteúdo adulto
concorrentes
assuntos jurídicos

(um por linha)`}
                                rows={5}
                                onChange={e =>
                                    updateGuardrails("forbidden_topics", e.target.value.split("\n"))
                                }
                            />
                        </FormGroup>

                        <FormGroupInline>
                            <FormGroup labelText="Tamanho máximo">
                                <Input
                                    type="number"
                                    value={form.guardrails.max_response_length}
                                    onChange={e =>
                                        updateGuardrails("max_response_length", Number(e.target.value))
                                    }
                                />
                                <small style={{ color: "var(--text-secondary)" }}>
                                    Limite de caracteres por resposta (ideal: entre 200 e 500 para WhatsApp)
                                </small>
                            </FormGroup>

                            <FormGroup labelText="Ocultar dados sensíveis nas respostas">
                                <Select
                                    options={optionsBoolean}
                                    selected={Number(form.guardrails.pii_masking ?? "")}
                                    onChange={val => updateGuardrails("pii_masking", val)}
                                />
                                <small style={{ color: "var(--text-secondary)" }}>
                                    Oculta dados sensíveis como CPF, e-mail e telefone nas respostas
                                </small>
                            </FormGroup>
                        </FormGroupInline>

                        <BreakForm title="Restrições de negócio" showAction={false} />

                        <Textarea
                            value={form.brain.constraints.join("\n")}
                            placeholder={`Ex:
Nunca oferecer desconto sem autorização
Não mencionar concorrentes
Não prometer prazos sem confirmação

(uma regra por linha)`}
                            rows={5}
                            onChange={e =>
                                setForm(prev => ({
                                    ...prev,
                                    brain: {
                                        ...prev.brain,
                                        constraints: e.target.value.split("\n")
                                    }
                                }))
                            }
                        />
                    </>
                )}

                {activeTab === "follow_up" && (
                    <>
                        <BreakForm title="Retomar conversas sem resposta" showAction={false} />
                        <div className={styles.guidance}>
                            <strong>Para que serve?</strong>
                            <span>Se o agente fizer uma pergunta e o cliente não responder, ele pode enviar até três lembretes em cada plataforma. Uma resposta do cliente ou a entrada de um atendente humano cancela os envios pendentes.</span>
                        </div>

                        <FormGroup labelText="Retomar conversas automaticamente?">
                            <Select
                                options={optionsBoolean}
                                selected={Number(form.follow_up?.enabled ?? false)}
                                onChange={value => updateFollowUp({ enabled: parseBooleanSelectValue(value) })}
                                allowDeselectAll={false}
                            />
                        </FormGroup>
                        {form.follow_up?.enabled && (
                            <>
                                {followUpMetrics && (
                                    <section className={styles.followUpMetrics}>
                                        <header><strong>Resultados dos últimos {followUpMetrics.period_days} dias</strong><span>Os números consideram somente este agente.</span></header>
                                        <div>
                                            <p><strong>{followUpMetrics.pending}</strong><span>Pendentes</span></p>
                                            <p><strong>{followUpMetrics.delivered}</strong><span>Enviadas</span></p>
                                            <p><strong>{followUpMetrics.responded}</strong><span>Respondidas</span></p>
                                            <p><strong>{followUpMetrics.response_rate}%</strong><span>Taxa de resposta</span></p>
                                            <p><strong>{followUpMetrics.cancelled}</strong><span>Canceladas</span></p>
                                        </div>
                                    </section>
                                )}
                                {!!agent?._id && (
                                    <section className={styles.followUpOperations}>
                                        <header><div><strong>Envios recentes</strong><span>Últimas 50 retomadas registradas por este agente.</span></div><Button type="button" variant="neutral" size="sm" fullWidth={false} onClick={loadFollowUpOperations}>Atualizar</Button></header>
                                        {!followUpMessages.length && <p className={styles.followUpOperationsEmpty}>Nenhuma retomada registrada até agora.</p>}
                                        {followUpMessages.map(message => (
                                            <div className={styles.followUpOperation} key={message._id}>
                                                <span className={styles.followUpOperationStatus}>{followUpStatusLabels[message.status] || "Registrada"}</span>
                                                <p><strong>{platformLabels[message.platform] || "Plataforma"}, tentativa {message.sequence || 1}</strong><small>{message.scheduled_date ? `Prevista para ${new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(message.scheduled_date))}` : `Registrada em ${new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(message.created_at))}`}</small></p>
                                                {message.status === "pending" && <Button type="button" variant="neutral" size="sm" fullWidth={false} onClick={() => cancelFollowUpMessage(message._id)}>Cancelar</Button>}
                                            </div>
                                        ))}
                                    </section>
                                )}
                                <BreakForm title="Dias e horários permitidos" showAction={false} />
                                <FormGroup labelText="Em quais dias pode enviar?">
                                    <Select
                                        name="Dias permitidos"
                                        options={weekdayOptions}
                                        selected={form.follow_up.allowed_weekdays || []}
                                        onChange={value => updateFollowUp({ allowed_weekdays: value.map(Number) })}
                                        multiple
                                        showOptionAll={false}
                                    />
                                </FormGroup>
                                <FormGroupInline className={styles.followUpTimeRow}>
                                    <FormGroup labelText="A partir de">
                                        <TimePicker
                                            name="Horário inicial"
                                            time={form.follow_up.start_time}
                                            onChange={value => updateFollowUp({ start_time: value || "08:00" })}
                                        />
                                    </FormGroup>
                                    <FormGroup labelText="Até">
                                        <TimePicker
                                            name="Horário final"
                                            time={form.follow_up.end_time}
                                            onChange={value => updateFollowUp({ end_time: value || "20:00" })}
                                        />
                                    </FormGroup>
                                </FormGroupInline>
                                <small className={styles.fieldExplanation}>Fora desse período, o lembrete aguarda o próximo horário permitido. Cada plataforma também aplica sua própria janela de atendimento.</small>

                                <BreakForm title="Quando interromper" showAction={false} />
                                <FormGroup labelText="Parar quando o lead for ganho?">
                                    <Select options={optionsBoolean} selected={Number(form.follow_up.stop_on_lead_won ?? true)} onChange={value => updateFollowUp({ stop_on_lead_won: parseBooleanSelectValue(value) })} allowDeselectAll={false} />
                                    <small className={styles.fieldExplanation}>Evita novos lembretes quando o lead vinculado já foi marcado como ganho.</small>
                                </FormGroup>
                                <FormGroup labelText="Parar quando o lead mudar de etapa?">
                                    <Select options={optionsBoolean} selected={Number(form.follow_up.stop_on_stage_change ?? false)} onChange={value => updateFollowUp({ stop_on_stage_change: parseBooleanSelectValue(value) })} allowDeselectAll={false} />
                                    <small className={styles.fieldExplanation}>Use quando qualquer avanço manual no funil significa que a retomada já cumpriu seu objetivo.</small>
                                </FormGroup>

                                <BreakForm title="Quando considerar que o cliente desistiu" showAction={false} />
                                <FormGroupInline className={styles.followUpTimeRow}>
                                    <FormGroup labelText="Quanto tempo depois da última tentativa?">
                                        <Input
                                            type="number"
                                            min="1"
                                            max={form.follow_up._abandonment_unit === "days" ? 30 : 720}
                                            step="1"
                                            value={Math.max(1, Math.round(Number(form.follow_up.abandonment_after_hours || 24) / abandonmentUnitHours[form.follow_up._abandonment_unit || "hours"]))}
                                            onChange={event => updateFollowUp({ abandonment_after_hours: Number(event.target.value) * abandonmentUnitHours[form.follow_up._abandonment_unit || "hours"] })}
                                        />
                                    </FormGroup>
                                    <FormGroup labelText="Unidade">
                                        <Select
                                            options={abandonmentDelayUnits}
                                            selected={form.follow_up._abandonment_unit || "hours"}
                                            onChange={value => updateFollowUp({ _abandonment_unit: value })}
                                        />
                                    </FormGroup>
                                </FormGroupInline>
                                <small className={styles.fieldExplanation}>Se não houver resposta nem atuação de um atendente humano nesse período, a conversa entra na métrica de abandono. Ela permanece disponível no Chat.</small>

                                <BreakForm
                                    title="Tentativas"
                                    iconAction={<HiPlus />}
                                    actionVariant="primary"
                                    handleAction={addFollowUpAttempt}
                                    showAction={(form.follow_up.attempts || []).length < 12 && [...new Set(availableInboxes.map(inbox => inbox.platform))].some(platform => (
                                        form.follow_up.attempts.filter(attempt => !attempt.platforms?.length || attempt.platforms.includes(platform)).length < 3
                                    ))}
                                />

                                {!(form.follow_up.attempts || []).length && (
                                    <div className={styles.followUpEmptyState}>
                                        <strong>Nenhuma tentativa configurada</strong>
                                        <span>Adicione quando o primeiro lembrete deve ser enviado e escreva a mensagem.</span>
                                        <Button type="button" variant="secondary" fullWidth={false} onClick={addFollowUpAttempt}><HiPlus /> Adicionar tentativa</Button>
                                    </div>
                                )}

                                {(form.follow_up.attempts || []).map((attempt, index) => (
                                    <section className={styles.followUpAttempt} key={index}>
                                        <header>
                                            <div><strong>Tentativa {index + 1}</strong><span>{index === 0 ? "Primeiro lembrete" : "Novo lembrete se o cliente continuar sem responder"}</span></div>
                                            <Button
                                                type="button"
                                                variant="neutral"
                                                size="sm"
                                                fullWidth={false}
                                                aria-label={`Remover tentativa ${index + 1}`}
                                                onClick={() => updateFollowUp({ attempts: form.follow_up.attempts.filter((_, attemptIndex) => attemptIndex !== index) })}
                                            ><MdOutlineDelete /></Button>
                                        </header>
                                        <FormGroupInline className={styles.followUpTimeRow}>
                                            <FormGroup labelText="Quanto tempo depois?">
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    max={attempt._delay_unit === "days" ? 30 : (attempt._delay_unit === "hours" ? 720 : 43200)}
                                                    step="1"
                                                    value={Math.max(1, Math.round(Number(attempt.after_minutes || 0) / delayUnitMinutes[attempt._delay_unit || "minutes"]))}
                                                    onChange={event => updateFollowUpAttempt(index, { after_minutes: Number(event.target.value) * delayUnitMinutes[attempt._delay_unit || "minutes"] })}
                                                />
                                            </FormGroup>
                                            <FormGroup labelText="Unidade">
                                                <Select
                                                    options={followUpDelayUnits}
                                                    selected={attempt._delay_unit || "minutes"}
                                                    onChange={value => updateFollowUpAttempt(index, { _delay_unit: value })}
                                                />
                                            </FormGroup>
                                        </FormGroupInline>
                                        <FormGroup labelText="Em quais plataformas?">
                                            <Select
                                                name="Todas as plataformas disponíveis"
                                                options={[...new Map(availableInboxes.map(inbox => [inbox.platform, { id: inbox.platform, value: platformLabels[inbox.platform] }])).values()]}
                                                selected={attempt.platforms || []}
                                                onChange={value => updateFollowUpAttempt(index, { platforms: value })}
                                                multiple
                                                showOptionAll={false}
                                            />
                                            <small className={styles.fieldExplanation}>A lista mostra somente canais conectados e liberados para este usuário. Deixe vazio para aplicar a todos eles.</small>
                                        </FormGroup>
                                        <FormGroup labelText="Qual mensagem deve ser enviada?">
                                            <Select
                                                options={followUpDeliveryOptions.filter(option => option.id === "text" || availableInboxes.some(inbox => inbox.platform === "whatsapp_api"))}
                                                selected={attempt.delivery_type || "text"}
                                                onChange={value => updateFollowUpAttempt(index, {
                                                    delivery_type: value,
                                                    platforms: ["whatsapp_template", "auto"].includes(value) ? ["whatsapp_api"] : attempt.platforms,
                                                    inbox_id: value === "text" ? "" : attempt.inbox_id,
                                                })}
                                            />
                                            {attempt.delivery_type === "text" && <small className={styles.fieldExplanation}>O texto será personalizado com os dados disponíveis, sem gerar uma nova resposta por IA. Messenger e Instagram Direct não permitem retomada automática depois de 24 horas.</small>}
                                        </FormGroup>
                                        <FormGroup labelText="Usar somente nestas intenções, opcional">
                                            <Select
                                                name="Todas as intenções"
                                                options={(form.brain?.intents || []).filter(intent => intent?.name).map(intent => ({ id: intent.name, value: intent.name }))}
                                                selected={attempt.intent_names || []}
                                                onChange={value => updateFollowUpAttempt(index, { intent_names: value })}
                                                multiple
                                                showOptionAll={false}
                                            />
                                            <small className={styles.fieldExplanation}>Deixe vazio para usar este lembrete em qualquer assunto.</small>
                                        </FormGroup>
                                        {["text", "auto"].includes(attempt.delivery_type || "text") && <FormGroup labelText={attempt.delivery_type === "auto" ? "Texto enviado quando a conversa ainda estiver aberta" : "Texto da mensagem"}>
                                            <Textarea
                                                value={attempt.message || ""}
                                                maxLength={500}
                                                onChange={event => updateFollowUpAttempt(index, { message: event.target.value })}
                                                placeholder="Ex: Conseguiu ver minha última mensagem? Posso continuar te ajudando por aqui."
                                            />
                                            <small className={styles.fieldExplanation}>Escreva somente a mensagem. O sistema pode incluir o primeiro nome do cliente automaticamente, quando ele estiver disponível.</small>
                                        </FormGroup>}
                                        {["text", "auto"].includes(attempt.delivery_type || "text") && <>
                                            <FormGroup labelText="Personalizar com o nome do cliente?">
                                                <Select
                                                    options={optionsBoolean.map(option => ({ ...option, value: option.id ? "Sim, incluir automaticamente" : "Não, enviar somente o texto" }))}
                                                    selected={Number(attempt.personalize_with_name !== false)}
                                                    onChange={value => updateFollowUpAttempt(index, { personalize_with_name: parseBooleanSelectValue(value) })}
                                                    allowDeselectAll={false}
                                                />
                                            </FormGroup>
                                            <section className={styles.messagePreview}>
                                                <small>Prévia da mensagem</small>
                                                <p>{getFollowUpTextPreview(attempt) || "A prévia aparecerá aqui."}</p>
                                            </section>
                                        </>}
                                        {["whatsapp_template", "auto"].includes(attempt.delivery_type) && (
                                            <>
                                                <FormGroup labelText="Caixa do WhatsApp API">
                                                    <Select
                                                        name="Selecione a caixa"
                                                        options={availableInboxes.filter(inbox => inbox.platform === "whatsapp_api").map(inbox => ({ id: inbox._id, value: inbox.name }))}
                                                        selected={attempt.inbox_id || ""}
                                                        onChange={value => {
                                                            updateFollowUpAttempt(index, { inbox_id: value, template_id: "", template_name: "", template_content: null, platforms: ["whatsapp_api"] })
                                                            loadTemplatesByInbox(value)
                                                        }}
                                                    />
                                                </FormGroup>
                                                <FormGroup labelText="Mensagem aprovada pelo WhatsApp">
                                                    <Select
                                                        name={attempt.inbox_id ? "Selecione o template" : "Selecione primeiro a caixa"}
                                                        options={(templatesByInbox[attempt.inbox_id] || []).map(template => ({ id: template.template_id, value: `${template.name} · ${template.category} · ${template.language}` }))}
                                                        selected={attempt.template_id || ""}
                                                        onChange={value => selectFollowUpTemplate(index, value)}
                                                        disabled={!attempt.inbox_id}
                                                    />
                                                </FormGroup>
                                                {!!attempt.template_id && <section className={styles.messagePreview}>
                                                    <small>Prévia da mensagem</small>
                                                    <p>{getFollowUpTemplatePreview(attempt) || "A prévia aparecerá aqui."}</p>
                                                </section>}
                                                {getFollowUpTemplateVariables(attempt).map((variable, variableIndex) => (
                                                    <FormGroup key={`${variable.component}-${variable.name}`} labelText={`Informação ${variableIndex + 1} da mensagem`}>
                                                        <Select
                                                            options={templateVariableSourceOptions}
                                                            selected={variable.source || "fixed"}
                                                            onChange={source => updateFollowUpTemplateVariable(index, variable, { source, value: source === "fixed" ? variable.value : "" })}
                                                        />
                                                        {(!variable.source || variable.source === "fixed") && <Input
                                                            value={variable.value}
                                                            maxLength={2000}
                                                            placeholder="Valor fixo enviado ao cliente"
                                                            onChange={event => updateFollowUpTemplateVariable(index, variable, { value: event.target.value })}
                                                        />}
                                                        {variable.source && variable.source !== "fixed" && <Input
                                                            value={variable.fallback || ""}
                                                            maxLength={200}
                                                            placeholder="Valor alternativo, ex: cliente"
                                                            onChange={event => updateFollowUpTemplateVariable(index, variable, { fallback: event.target.value })}
                                                        />}
                                                        {variable.source && variable.source !== "fixed" && <small className={styles.fieldExplanation}>Se essa informação não estiver cadastrada, o valor alternativo será usado. Se ambos estiverem vazios, a mensagem não será enviada.</small>}
                                                    </FormGroup>
                                                ))}
                                                {!!attempt.template_id && <small className={styles.fieldExplanation}>Antes do envio, o sistema confirma novamente se essa mensagem continua aprovada e pertence à caixa escolhida.</small>}
                                            </>
                                        )}
                                    </section>
                                ))}
                                {!!form.follow_up.attempts?.length && (
                                    <section className={styles.followUpTimeline}>
                                        <header><strong>Simulação da sequência</strong><span>Considerando que o cliente não respondeu e nenhum atendente assumiu.</span></header>
                                        {[...form.follow_up.attempts].sort((a, b) => Number(a.after_minutes) - Number(b.after_minutes)).flatMap((attempt, attemptIndex) => {
                                            const platforms = attempt.platforms?.length ? attempt.platforms : [...new Set(availableInboxes.map(inbox => inbox.platform))]
                                            return platforms.map((platform, platformIndex) => {
                                                const outsideWindow = Number(attempt.after_minutes) >= 1440
                                                const usesTemplateOutsideWindow = platform === "whatsapp_api" && ["auto", "whatsapp_template"].includes(attempt.delivery_type)
                                                const windowResult = outsideWindow
                                                    ? (usesTemplateOutsideWindow ? "Pode enviar com o template aprovado" : "Será cancelada se a janela estiver fechada")
                                                    : "Pode enviar enquanto a janela estiver aberta"
                                                const deliveryLabel = attempt.delivery_type === "auto" ? `Automático, template ${attempt.template_name || "não selecionado"}` : (attempt.delivery_type === "whatsapp_template" ? `Template ${attempt.template_name || "não selecionado"}` : "Mensagem normal")
                                                return <div key={`${attempt.after_minutes}-${attemptIndex}-${platformIndex}`}><span>{attemptIndex + 1}</span><p><strong>{platformLabels[platform] || platform}, após {formatFollowUpDelay(attempt.after_minutes)}</strong><small>{deliveryLabel}, {windowResult}</small></p></div>
                                            })
                                        })}
                                        <small>Esta é uma prévia. Horários indisponíveis adiam o envio, e a janela real é validada novamente antes de enviar.</small>
                                    </section>
                                )}
                            </>
                        )}
                    </>
                )}

                {activeTab === "handoff" && (
                    <>
                        <BreakForm title="Atendimento humano" showAction={false} />
                        <section className={styles.humanHelpGroup}>
                            <header>
                                <strong>Dúvidas antes de transferir</strong>
                                <span>O agente pode confirmar o pedido com o cliente antes de chamar um atendente humano.</span>
                            </header>

                            <FormGroup labelText="Com que frequência deve confirmar o que entendeu?">
                                <Select
                                    options={humanHelpTimingOptions}
                                    selected={humanHelpTiming}
                                    onChange={(value) => handleChange("settings", {
                                        ...form.settings,
                                        confidence_threshold: confidenceByHelpTiming[value]
                                    })}
                                />
                                <small className={styles.fieldExplanation}>
                                    {humanHelpTiming === "autonomous" && "Pergunta menos e tenta continuar sozinho na maioria das mensagens."}
                                    {humanHelpTiming === "balanced" && "Confirma somente quando houver uma chance real de ter entendido errado."}
                                    {humanHelpTiming === "cautious" && "Confirma até dúvidas pequenas antes de continuar o atendimento."}
                                </small>
                            </FormGroup>

                            <FormGroup labelText="Quantas vezes deve tentar entender?">
                                <Select
                                    options={clarificationAttemptOptions}
                                    selected={Number(form.settings.clarification_attempts ?? 2)}
                                    onChange={(value) => handleChange("settings", {
                                        ...form.settings,
                                        clarification_attempts: Number(value)
                                    })}
                                />
                                <small className={styles.fieldExplanation}>Primeiro confirma o que entendeu. Se a dúvida continuar, pede ao cliente que explique de outra forma.</small>
                            </FormGroup>

                            <FormGroup labelText="Se ainda não entender depois dessas tentativas">
                                <Select
                                    options={doubtBehaviorOptions}
                                    selected={Number(form.settings.fallback_to_human ?? "")}
                                    onChange={(val) => handleChange("settings", { ...form.settings, fallback_to_human: val })}
                                />
                            </FormGroup>

                            <FormGroup labelText="Se a informação não estiver nos conteúdos do agente">
                                <Select
                                    options={knowledgeGapOptions}
                                    selected={Number(form.settings.knowledge_gap_handoff ?? true)}
                                    onChange={(value) => handleChange("settings", {
                                        ...form.settings,
                                        knowledge_gap_handoff: value
                                    })}
                                />
                                <small className={styles.fieldExplanation}>Isso vale quando o agente entendeu a pergunta, mas não encontrou informação suficiente para respondê-la sem inventar.</small>
                            </FormGroup>
                        </section>

                        <BreakForm
                            title="Para onde transferir"
                            iconAction={<><HiPlus /> Adicionar regra</>}
                            actionVariant="primary"
                            handleAction={() => {
                                const rules = [...(form.handoff?.rules || [])];
                                rules.push({ when: { intents: [] }, target: { assignTo: "teamAndRole", team_id: "", role_id: "", users: [] } });
                                setForm(prev => ({ ...prev, handoff: { ...prev.handoff, rules } }));
                            }}
                            showAction
                        />

                        {form.handoff?.rules?.length === 0 && (
                            <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem", textAlign: "center", padding: "10px" }}>
                                Nenhuma regra de transferência configurada.
                            </p>
                        )}

                        {form.handoff?.rules?.map((rawRule, rIdx) => {
                            const rule = getFormattedHandoffRule(rawRule)

                            return (
                            <div key={rIdx} className={styles.configurationCard}>
                                <header className={styles.cardHeader}>
                                    <h4>Regra #{rIdx + 1}</h4>
                                    <Button type="button" variant="neutral" size="sm" fullWidth={false} onClick={() => {
                                            const rules = form.handoff.rules.filter((_, i) => i !== rIdx);
                                            setForm(prev => ({ ...prev, handoff: { ...prev.handoff, rules } }));
                                        }}><MdOutlineDelete /> Remover</Button>
                                </header>

                                <div>
                                    <FormGroup labelText="Se detectar as intenções">
                                        <Select
                                            name="Intenções selecionadas"
                                            emptyName="Qualquer intenção"
                                            placeholder="Pesquisar intenção..."
                                            options={(form.brain?.intents || [])
                                                .filter(intent => intent?.name)
                                                .map(intent => ({
                                                    id: intent.name,
                                                    value: intent.description ? `${intent.name} - ${intent.description}` : intent.name
                                                }))}
                                            selected={rule.when?.intents || []}
                                            multiple={true}
                                            showOptionAll={false}
                                            onChange={(value) => {
                                                updateHandoffRule(rIdx, current => ({
                                                    ...getFormattedHandoffRule(current),
                                                    when: {
                                                        ...getFormattedHandoffRule(current).when,
                                                        intents: value
                                                    }
                                                }))
                                            }}
                                        />
                                        <small style={{ color: "var(--text-secondary)" }}>Deixe vazio para usar como regra geral quando nenhuma regra específica se aplicar.</small>
                                    </FormGroup>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginTop: "10px" }}>
                                    <FormGroup labelText="Transferir para">
                                        <Select
                                            selected={rule.target?.assignTo}
                                            options={listAssignTo}
                                            onChange={(val) => {
                                                updateHandoffRule(rIdx, current => ({
                                                    ...getFormattedHandoffRule(current),
                                                    target: {
                                                        assignTo: val,
                                                        team_id: "",
                                                        role_id: "",
                                                        users: []
                                                    }
                                                }))
                                            }}
                                        />
                                    </FormGroup>

                                    {rule.target?.assignTo === "teamAndRole" && (
                                        <FormGroupInline>
                                            <FormGroup labelText="Equipe">
                                                <Select
                                                    name="Equipe"
                                                    placeholder="Pesquisar..."
                                                    options={teams}
                                                    selected={rule.target?.team_id || ""}
                                                    onChange={(value) => {
                                                        updateHandoffRule(rIdx, current => ({
                                                            ...getFormattedHandoffRule(current),
                                                            target: { ...getFormattedHandoffRule(current).target, team_id: value }
                                                        }))
                                                    }}
                                                />
                                            </FormGroup>
                                            <FormGroup labelText="Cargo">
                                                <Select
                                                    name="Cargo"
                                                    placeholder="Pesquisar..."
                                                    options={roles}
                                                    selected={rule.target?.role_id || ""}
                                                    onChange={(value) => {
                                                        updateHandoffRule(rIdx, current => ({
                                                            ...getFormattedHandoffRule(current),
                                                            target: { ...getFormattedHandoffRule(current).target, role_id: value }
                                                        }))
                                                    }}
                                                />
                                            </FormGroup>
                                        </FormGroupInline>
                                    )}

                                    {rule.target?.assignTo === "users" && (
                                        <FormGroup labelText="Usuários">
                                            <Select
                                                name="Usuários"
                                                placeholder="Pesquisar..."
                                                options={users}
                                                selected={rule.target?.users || []}
                                                multiple={true}
                                                onChange={(value) => {
                                                    updateHandoffRule(rIdx, current => ({
                                                        ...getFormattedHandoffRule(current),
                                                        target: { ...getFormattedHandoffRule(current).target, users: value }
                                                    }))
                                                }}
                                            />
                                        </FormGroup>
                                    )}
                                </div>
                            </div>
                        )})}
                    </>
                )}

                {activeTab === "versions" && (
                    <>
                        <BreakForm title="Histórico de versões" showAction={false} />
                        <div className={styles.guidance}>
                            <strong>Consulte ou restaure configurações anteriores</strong>
                            <span>Cada alteração salva cria uma nova versão. Restaurar não apaga o histórico atual.</span>
                        </div>
                        {!agent?._id ? (
                            <section className={styles.fixedResponseEmptyState}>
                                <span><MdOutlineHistory /></span>
                                <div>
                                    <strong>O histórico começa depois do primeiro salvamento</strong>
                                    <p>Crie o agente para registrar e comparar as próximas versões.</p>
                                </div>
                            </section>
                        ) : (
                            <section className={styles.versionHistory}>
                                {!agentVersions.length && <p>Nenhuma versão anterior disponível.</p>}
                                {agentVersions.map(version => <div key={version._id}>
                                    <span><strong>Versão {version.version}</strong><small>{version.reason || "Atualização do agente"}, {new Date(version.created_at).toLocaleString("pt-BR")}</small><small>{version.snapshot?.brain?.intents?.length || 0} intenções, {version.snapshot?.knowledge_base_ids?.length || 0} bases, {version.snapshot?.fixed_responses?.length || 0} respostas prontas</small></span>
                                    <Button type="button" variant="neutral" size="sm" fullWidth={false} onClick={() => setVersionToRestore(version)}>Comparar e restaurar</Button>
                                </div>)}
                            </section>
                        )}
                    </>
                )}

                    </section>
                    <footer className={styles.actions}>
                    <Button variant="primary" onClick={handleSubmit}>
                        {agent?._id ? "Salvar nova versão" : "Criar agente"}
                    </Button>
                    <Button type="button" variant="secondary" onClick={requestCloseModal}>
                        Cancelar
                    </Button>
                    </footer>
                </main>
                </div>
            </form >
        </Modal >
        {versionToRestore && <ModalAgentVersionComparison
            agent={agent}
            fixedResponses={fixedResponses}
            version={versionToRestore}
            restoring={isRestoringVersion}
            closeModal={() => !isRestoringVersion && setVersionToRestore(null)}
            onRestore={restoreAgentVersion}
        />}
        {confirmExit && <ModalConfirm
            icon={<MdWarningAmber />}
            title="Sair sem salvar?"
            description="Existem alterações no agente que ainda não foram salvas."
            text="Se você sair agora, todo o progresso feito desde que abriu este agente será perdido."
            confirmLabel="Sair e descartar alterações"
            confirmVariant="danger"
            closeModal={() => setConfirmExit(false)}
            onSubmit={discardChangesAndClose}
        />}
    </>)
}

export default ModalAgent
